import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { useHasFilter } from '@/hooks/useHasFilter';

import { ISearchWallet } from '@/features/wallets/store/models/wallet.model';
import {
  useAllWalletsTypeIdField,
  mapAllWalletsTypeId,
} from '@/features/wallets/hooks/AllWallets/useAllWalletsTypeIdField';
import {
  useAllWalletsCreatedField,
  mapAllWalletsCreated,
} from '@/features/wallets/hooks/AllWallets/useAllWalletsCreatedField';
import {
  useAllWalletsSearchField,
  mapAllWalletsSearch,
} from '@/features/wallets/hooks/AllWallets/useAllWalletsSearchField';
import {
  useAllWalletsStatusField,
  mapAllWalletsStatus,
} from '@/features/wallets/hooks/AllWallets/useAllWalletsStatusField';
import { parseAllParameters, TUseFiltersdReturn } from '@/ui-kit/table-filters/filters.type';
import {
  IAllWalletsFiltersFormFields,
  TAllWalletsFiltersFields,
} from '@/features/wallets/store/types/agent-wallets.types';

export const parseAllWalletsFiltersValue = (type: keyof IAllWalletsFiltersFormFields, value): ISearchWallet => {
  if (value === undefined || value === null) {
    return undefined;
  }
  if (type === 'createdDate') {
    return mapAllWalletsCreated(value);
  } else if (type === 'status') {
    return mapAllWalletsStatus(value);
  } else if (type === 'search') {
    return mapAllWalletsSearch(value);
  } else if (type === 'typeId') {
    return mapAllWalletsTypeId(value);
  }
};

export function useAllWalletsFilters(
  onChange: (queryParameters: ISearchWallet) => void,
  initialValues: Partial<IAllWalletsFiltersFormFields> = {},
): TUseFiltersdReturn<TAllWalletsFiltersFields> {
  const methods = useForm<IAllWalletsFiltersFormFields>({
    defaultValues: initialValues,
  });

  const getQueryParameters = useCallback(() => {
    const values = methods.getValues();
    return parseAllParameters<IAllWalletsFiltersFormFields, ISearchWallet>(values, parseAllWalletsFiltersValue);
  }, [methods]);

  const filtersDeps = JSON.stringify(initialValues);

  useEffect(() => {
    const values = JSON.parse(filtersDeps);
    Object.keys(values).map((key: keyof IAllWalletsFiltersFormFields) => {
      methods.setValue(key, values[key]);
    });
  }, [filtersDeps, methods]);

  const handleChange = useCallback(() => {
    onChange(getQueryParameters());
  }, [getQueryParameters, onChange]);

  const handleClear = useCallback(() => {
    methods.reset({ search: null, typeId: 9 });
    onChange(getQueryParameters());
  }, [methods, getQueryParameters, onChange]);

  const hasFilters = useHasFilter({});

  const typeId = useAllWalletsTypeIdField(methods, handleChange);
  const createdDate = useAllWalletsCreatedField(methods, handleChange);
  const search = useAllWalletsSearchField(methods, handleChange);
  const status = useAllWalletsStatusField(methods, handleChange);

  return { status, typeId, createdDate, search, clearFilters: handleClear, hasFilters };
}
